import { useMemo, useState, useCallback, useEffect, ErrorInfo } from "react";

import { defineMessage, useIntl } from "react-intl";

import { useLoggedInUser } from "@/utils/logged-in-user-provider";

import { ProductRecommendationProps } from "../common/types";

import { RecommendationsViewedProps, useAnalytics } from "./use-analytics";
import { useFetchRecommendations } from "./use-fetch-recommendations";
import { useHasEnterpriseEdition } from "./use-has-enterprise-edition";

export interface UseGlobalNavProductRecommendationsResponse
    extends ProductRecommendationProps {
    onGlobalNavProductRecommendationsFailed: (
        error: Error,
        errorInfo: ErrorInfo
    ) => void;
    shouldShowGlobalNavProductRecommendations: boolean;
    mobileNavPageLabelFeaturedProducts: string;
    fireGlobalNavProductRecoEventsWhenInView: ({
        isMobile
    }: Pick<RecommendationsViewedProps, "isMobile">) => void;
}

export function useGlobalNavProductRecommendations(): UseGlobalNavProductRecommendationsResponse {
    const [
        isGlobalNavProductRecommendationsExperimentFailed,
        setIsGlobalNavProductRecommendationsExperimentFailed
    ] = useState(false);

    const { isUserLoggedIn } = useLoggedInUser();
    const intl = useIntl();
    const hasEnterpriseEdition = useHasEnterpriseEdition();
    const {
        getProductRecommendations,
        isRecommendationLoading,
        recommendations
    } = useFetchRecommendations();

    const {
        fireRenderGlobalNavProductRecommendationsFailed,
        fireGlobalNavProductRecommendationsViewed
    } = useAnalytics();

    const onGlobalNavProductRecommendationsFailed = useCallback(
        (error: Error, errorInfo: ErrorInfo) => {
            fireRenderGlobalNavProductRecommendationsFailed(error, errorInfo);
            setIsGlobalNavProductRecommendationsExperimentFailed(true);
        },
        [fireRenderGlobalNavProductRecommendationsFailed]
    );

    useEffect(() => {
        if (isUserLoggedIn && !hasEnterpriseEdition) {
            getProductRecommendations({
                setIsGlobalNavProductRecommendationsExperimentFailed
            });
        }
    }, [hasEnterpriseEdition, getProductRecommendations, isUserLoggedIn]);

    const fireGlobalNavProductRecoEventsWhenInView = useCallback(
        ({ isMobile }: Pick<RecommendationsViewedProps, "isMobile">) => {
            try {
                if (
                    isUserLoggedIn &&
                    !hasEnterpriseEdition &&
                    recommendations.length > 0
                ) {
                    fireGlobalNavProductRecommendationsViewed({
                        recommendations,
                        isMobile
                    });
                }
            } catch (_) {
                // Do nothing
            }
        },
        [
            recommendations,
            hasEnterpriseEdition,
            isUserLoggedIn,
            fireGlobalNavProductRecommendationsViewed
        ]
    );

    /**
     * Show the Global Nav Product Recommendations if the user is eligible, and the recommendations are loaded.
     */
    const shouldShowGlobalNavProductRecommendations = useMemo(() => {
        const nonEmptyRecosLoaded =
            !isRecommendationLoading && recommendations.length > 0;

        const shouldShowByRecommendationState =
            isRecommendationLoading || nonEmptyRecosLoaded;

        return (
            isUserLoggedIn &&
            shouldShowByRecommendationState &&
            !isGlobalNavProductRecommendationsExperimentFailed &&
            !hasEnterpriseEdition
        );
    }, [
        hasEnterpriseEdition,
        isGlobalNavProductRecommendationsExperimentFailed,
        isRecommendationLoading,
        isUserLoggedIn,
        recommendations.length
    ]);

    const mobileNavPageLabelFeaturedProducts = useMemo(() => {
        return intl.formatMessage(
            defineMessage({
                id: "globalnav.v2.productseveryone.title",
                defaultMessage: "Featured Products"
            })
        );
    }, [intl]);

    return {
        onGlobalNavProductRecommendationsFailed,
        shouldShowGlobalNavProductRecommendations,
        recommendations,
        isRecommendationLoading,
        mobileNavPageLabelFeaturedProducts,
        fireGlobalNavProductRecoEventsWhenInView
    };
}
